import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { forceReloadToLatest } from '@/lib/appUpdate'
import { checkAppStoreVersion, openAppStoreListing } from '@/lib/appStoreUpdate'
import {
  applyPendingCapgoUpdate,
  isWorkoutInProgress,
  consumePendingCapgoApply,
  getPendingCapgoVersion,
  readPendingCapgoApplyVersion,
  recordPendingCapgoApply,
  requestApplyOnDownload,
} from '@/lib/seamlessUpdate'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { RefreshCw, Download, X } from 'lucide-react'

const DISMISSED_KEY_PREFIX = 'update_dismissed_'

type UpdateKind = 'app-store' | 'native-capgo' | 'web-capgo' | null

interface UpdateState {
  kind: UpdateKind
  /** Marketing-friendly label for the dismiss key — distinct per update
   *  so dismissing one bundle doesn't suppress the next. */
  versionTag: string
  detail: string | null
}

/**
 * One unified "update available" toast for every channel:
 *   - App Store (native IPA, requires Apple review) — wins priority. A
 *     new IPA may carry native code (audio session, plugins, permissions)
 *     that Capgo bundles can't deliver, so users should update the
 *     native side before worrying about the JS one.
 *   - Native Capgo (OTA JS bundle on iOS / Android).
 *   - Web Capgo (JS bundle in a browser tab).
 *
 * Render path: fixed bottom toast, persistent until the user updates or
 * dismisses. Dismissal is per-version so a NEW update reopens the toast
 * even if the user dismissed the previous one. Mounted at App root.
 */
export function VersionUpdateBanner() {
  const { hasUpdate: webHasUpdate, currentVersion, latestVersion } = useVersionCheck()
  const isNative = Capacitor.isNativePlatform()
  const { toast } = useToast()

  // Post-launch confirmation (option D, 2026-06-20). If the prior
  // session's in-app set() failed, we silently recorded the version
  // it was trying to apply. Capgo's autoUpdate at native cold-start
  // would then apply it on the next process launch — at which point
  // `consumePendingCapgoApply()` confirms the running bundle matches
  // and returns the version so we can show a quiet "✓ Updated to
  // vX" toast. Replaces the prior "Update couldn't apply
  // automatically — close the app fully…" alert that was making the
  // app feel broken (founder report 2026-06-20: the alert kept
  // recurring after each round of fixes; the underlying flakiness of
  // WKWebView's in-app reload isn't worth fighting any further).
  useEffect(() => {
    let cancelled = false
    consumePendingCapgoApply().then((appliedVersion) => {
      if (cancelled || !appliedVersion) return
      // Bundle landed on this cold launch — clear the "pending" pill
      // state and surface the confirmation toast.
      setPendingApply(null)
      toast({
        title: '✓ Updated',
        description: `You're now on v${appliedVersion}.`,
      })
    }).catch(() => { /* lookup failure is non-fatal — try next launch */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // App Store update (native only).
  const [appStore, setAppStore] = useState<{
    hasUpdate: boolean; storeVersion: string | null;
  }>({ hasUpdate: false, storeVersion: null })

  // Capgo bundle update on native — separate from the web version check
  // (which fetches /version.json, always equal to the running bundle on
  // native because Capgo OTA replaces the whole bundle including that
  // file). Asks the Capgo plugin for the channel's latest vs current.
  const [capgoNative, setCapgoNative] = useState<{
    hasUpdate: boolean; latest: string | null;
  }>({ hasUpdate: false, latest: null })

  useEffect(() => {
    if (!isNative) return
    let cancelled = false
    const runCheck = async () => {
      // Force-refresh on every call (not just first mount) so a user
      // who opened the app pre-release and brought it back to foreground
      // post-release picks up the new banner without a force-quit.
      // Founder report 2026-06-14: wife stuck on 1.1.6 with no banner
      // because the cached "no update" answer from morning never
      // re-checked. checkAppStoreVersion({ force: true }) bypasses the
      // session-cache short-circuit and re-hits iTunes Lookup.
      const state = await checkAppStoreVersion({ force: true })
      if (!cancelled) setAppStore({ hasUpdate: state.hasUpdate, storeVersion: state.storeVersion })
    }
    runCheck()
    // Re-check on background→foreground (the most common "I came back to
    // the app after Apple released" trigger).
    let removeListener: (() => void) | null = null
    import('@capacitor/app')
      .then(({ App: CapApp }) =>
        CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) runCheck()
        }),
      )
      .then((handle) => {
        if (cancelled) { handle.remove().catch(() => {}); return }
        removeListener = () => { handle.remove().catch(() => {}) }
      })
      .catch(() => { /* listener attach failed — first-mount check still ran */ })
    return () => {
      cancelled = true
      if (removeListener) removeListener()
    }
  }, [isNative])

  // Re-runs on foreground transitions and every 60s so a user who
  // happened to open the app right before a deploy doesn't sit on the
  // stale bundle indefinitely. The one-shot-on-mount version of this
  // effect caused the same "You're on the latest version" lie that
  // bit AppVersionFooter.
  useEffect(() => {
    if (!isNative) return
    let cancelled = false
    const runCheck = async () => {
      try {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
        const [current, latest] = await Promise.all([
          CapacitorUpdater.current(),
          (CapacitorUpdater as unknown as {
            getLatest: () => Promise<{ version?: string }>
          }).getLatest(),
        ])
        if (cancelled) return
        const running = current?.bundle?.version
        const channelLatest = latest?.version
        if (running && channelLatest && running !== channelLatest) {
          setCapgoNative({ hasUpdate: true, latest: channelLatest })
        } else if (running && channelLatest && running === channelLatest) {
          // Bundle caught up — clear any stale "update available" state
          // from a previous check so dismissing the banner sticks.
          setCapgoNative({ hasUpdate: false, latest: null })
        }
      } catch {
        /* plugin missing / offline / unsupported — leave default */
      }
    }
    // Defer the FIRST runCheck by 6 seconds on cold start. This avoids
    // a TestFlight crash (D32F3CEC, build 65, iOS 26.5.1) where the
    // mount-time getLatest() raced Capgo's own autoUpdate background-
    // download getLatest() inside Alamofire's Session.request(). Both
    // calls went through the same Swift weak-reference machinery
    // simultaneously and tripped a SIGTRAP (swift_unknownObjectWeakAssign)
    // <1s after app launch. Capgo's autoUpdate completes well within
    // 6s; this delay lets that path finish before we add a second
    // caller. The 60s interval below is unchanged — only the first
    // call is delayed.
    const firstCallTimer = setTimeout(runCheck, 6_000)
    const interval = setInterval(runCheck, 60_000)
    let removeListener: (() => void) | null = null
    import('@capacitor/app')
      .then(({ App: CapApp }) =>
        CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) runCheck()
        }),
      )
      .then((handle) => {
        if (cancelled) { handle.remove().catch(() => {}); return }
        removeListener = () => { handle.remove().catch(() => {}) }
      })
      .catch(() => { /* degrade to interval-only */ })
    return () => {
      cancelled = true
      clearTimeout(firstCallTimer)
      clearInterval(interval)
      if (removeListener) removeListener()
    }
  }, [isNative])

  // Determine which update to surface. App Store wins; otherwise Capgo;
  // otherwise web Capgo (web only).
  const update: UpdateState | null = (() => {
    if (isNative && appStore.hasUpdate) {
      return {
        kind: 'app-store',
        versionTag: `appstore:${appStore.storeVersion ?? 'unknown'}`,
        detail: appStore.storeVersion ? `v${appStore.storeVersion} on the App Store` : null,
      }
    }
    if (isNative && capgoNative.hasUpdate) {
      return {
        kind: 'native-capgo',
        versionTag: `capgo-native:${capgoNative.latest ?? 'unknown'}`,
        detail: capgoNative.latest ? `Bundle ${capgoNative.latest} ready` : null,
      }
    }
    if (!isNative && webHasUpdate) {
      return {
        kind: 'web-capgo',
        versionTag: `capgo-web:${latestVersion ?? 'unknown'}`,
        detail: webDetail(currentVersion, latestVersion),
      }
    }
    return null
  })()

  const dismissKey = update ? `${DISMISSED_KEY_PREFIX}${update.versionTag}` : null

  const [dismissed, setDismissed] = useState(false)
  const [reloading, setReloading] = useState(false)

  // Burst-snooze: after the user taps Update on a Capgo (OTA JS) bundle,
  // suppress the Capgo pill for a short window so a rapid series of
  // deploys (each a new bundle version) doesn't immediately re-nag with a
  // fresh "Update ready" the moment they've acted. Stored in
  // sessionStorage so it NEVER outlives the app process — a force-quit /
  // cold launch clears it, meaning updates can never be permanently
  // hidden, and Capgo's autoUpdate applies the newest bundle on that same
  // cold launch anyway. App Store updates are intentionally NOT snoozed.
  const CAPGO_SNOOZE_KEY = 'capgo_pill_snooze_until'
  const CAPGO_SNOOZE_MS = 10 * 60 * 1000
  const [snoozeUntil, setSnoozeUntil] = useState<number>(() => {
    try { return Number(sessionStorage.getItem(CAPGO_SNOOZE_KEY)) || 0 } catch { return 0 }
  })
  const snoozeCapgoPill = () => {
    const until = Date.now() + CAPGO_SNOOZE_MS
    try { sessionStorage.setItem(CAPGO_SNOOZE_KEY, String(until)) } catch { /* ignore */ }
    setSnoozeUntil(until)
  }
  // True when a prior tap on Update tried the in-app swap and failed —
  // the downloaded bundle is on disk and will apply on the next cold
  // launch. We render a quieter "Update will finish next time you open
  // the app" pill in this state (no action button) so a user who taps
  // the update pill and then sees it visibly disappear isn't left
  // wondering if anything happened. Persists across renders via the
  // same localStorage record consumePendingCapgoApply reads — clears
  // automatically when the next mount finds the bundle has applied.
  const [pendingApply, setPendingApply] = useState<string | null>(() =>
    readPendingCapgoApplyVersion(),
  )
  // Tracks the in-flight safety timer for the Update button. Kept in a
  // ref (not state) so we can clear it from anywhere — on user dismiss,
  // on component unmount, or when the apply resolves — without
  // triggering a re-render. Without this cleanup the timer kept firing
  // after the banner was dismissed / unmounted, calling setState on a
  // dead component and triggering a React warning (bug-review
  // 2026-06-18 finding #1).
  const safetyTimerRef = useRef<number | null>(null)
  useEffect(() => {
    return () => {
      if (safetyTimerRef.current !== null) {
        window.clearTimeout(safetyTimerRef.current)
        safetyTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!dismissKey || typeof localStorage === 'undefined') {
      setDismissed(false)
      return
    }
    try {
      setDismissed(localStorage.getItem(dismissKey) === '1')
    } catch {
      setDismissed(false)
    }
  }, [dismissKey])

  // Snooze only the ACTIVE "Update ready" Capgo pill — never the App Store
  // one, and never the feedback states the user needs after they've tapped
  // Update: the "Updating…" in-flight state (`reloading`) or the quieter
  // "will finish next time you open the app" pill (`pendingApply`). Without
  // those exemptions the snooze hid all feedback for 10 min, re-introducing
  // the "did my tap do anything?" ambiguity (review 2026-07-28).
  const capgoSnoozed =
    (update?.kind === 'native-capgo' || update?.kind === 'web-capgo') &&
    !pendingApply &&
    !reloading &&
    Date.now() < snoozeUntil

  if (!update || dismissed || capgoSnoozed) return null

  const persistDismiss = () => {
    setDismissed(true)
    if (!dismissKey) return
    try { localStorage.setItem(dismissKey, '1') } catch { /* ignore */ }
  }

  // Action button label + handler + icon per update kind.
  const variant = (() => {
    switch (update.kind) {
      case 'app-store':
        return {
          title: 'App Store update available',
          buttonLabel: 'Open',
          onAction: openAppStoreListing,
          Icon: Download,
          // Don't allow dismiss on App Store updates — they carry native
          // code (e.g. the AVAudioSession fix) that's the whole reason
          // they shipped. Users can re-dismiss the Capgo banner if they
          // want, but the App Store banner stays until they update.
          allowDismiss: false,
        }
      case 'native-capgo':
        // After a silent fallback (in-app swap failed), the bundle is
        // on disk and will apply on the next cold launch. Show a
        // quieter pill so the user knows the tap registered and what
        // happens next — instead of the pill just vanishing.
        //
        // BUT: only when the pending record matches the version we're
        // currently offering. Otherwise a stale record from a prior
        // version (e.g. user skipped a release between cold launches)
        // would lock the pill into pending mode and prevent them from
        // tapping Update for the NEW version. Stale record = fall
        // through to the normal active pill; the record ages out via
        // its 7-day TTL or the next match in consumePendingCapgoApply.
        if (pendingApply && capgoNative.latest && pendingApply === capgoNative.latest) {
          return {
            title: 'Update will finish next time you open the app',
            buttonLabel: null,
            onAction: () => {},
            Icon: RefreshCw,
            allowDismiss: true,
          }
        }
        return {
          title: 'Update ready',
          buttonLabel: reloading ? 'Updating…' : 'Update',
          onAction: () => {
            // Option D, 2026-06-20: silent fallback instead of the
            // "Update couldn't apply automatically" alert. iOS WKWebView
            // doesn't reliably swap a JS bundle in-process — the in-app
            // set() succeeds maybe half the time, and the prior alert
            // made every failure feel like the app was broken. New
            // flow:
            //   1. Try the in-app swap (same as before — when it works,
            //      it's the fastest path).
            //   2. On failure (returned false, threw, or the 12s
            //      safety timer expired): silently record which version
            //      we were trying to apply, then dismiss the pill. The
            //      bundle is still on disk; Capgo's autoUpdate WILL
            //      apply it at the next natural cold launch — which
            //      almost every user does within hours.
            //   3. The next launch's consumePendingCapgoApply() effect
            //      detects the version is now running and fires a
            //      brief "✓ Updated to vX" toast. User sees confirmation
            //      that the update landed; no scary alert, no
            //      cold-restart instructions, no broken-feeling moment.
            //
            // We bumped the safety timer 5s → 12s because the legit
            // in-app swap takes 8-15s on cellular and we don't want
            // the silent-dismiss to fire while the real reload is in
            // flight. If the reload genuinely takes longer than 12s,
            // worst case is the user sees the pill dismiss right
            // before the new bundle loads anyway — no harm done.
            if (reloading) return
            // A live workout must never be reloaded out from under the user
            // — even by their own Update tap (founder lost a workout to
            // exactly this, 2026-07-03). The bundle applies automatically
            // right after the workout ends.
            if (isWorkoutInProgress()) {
              window.alert("You have a workout in progress — the update will install automatically after you finish it.")
              return
            }
            // Snooze the Capgo pill for a short window so back-to-back
            // deploys don't re-nag the moment the user has acted.
            snoozeCapgoPill()
            // Record the update intent so that if the bundle still needs
            // downloading, the downloadComplete listener applies it
            // automatically — removes the "needs a second tap" case.
            requestApplyOnDownload()
            setReloading(true)
            let alreadyHandled = false
            const silentlyFallBack = () => {
              if (alreadyHandled) return
              alreadyHandled = true
              if (safetyTimerRef.current !== null) {
                window.clearTimeout(safetyTimerRef.current)
                safetyTimerRef.current = null
              }
              setReloading(false)
              // Mark the bundle so the next cold launch can confirm it
              // landed via the post-launch toast.
              // Prefer the bundle id surfaced by Capgo's
              // downloadComplete listener (most precise), but fall
              // back to the channel-latest the version-check just
              // detected. Without this fallback, a cold-launch user
              // whose previous session already downloaded the bundle
              // would record nothing (the in-memory `pending` var is
              // session-scoped, not persisted), so neither the
              // pending pill nor the post-launch toast would fire.
              const v = getPendingCapgoVersion() ?? capgoNative.latest ?? undefined
              recordPendingCapgoApply(v)
              // Flip the pill into the quieter "Update will finish next
              // time you open the app" state instead of fully
              // dismissing it. Users who watch the pill (founder
              // included) shouldn't be left guessing whether their tap
              // did anything. The pill auto-clears on the next mount
              // once consumePendingCapgoApply confirms the bundle
              // applied; user can still X it manually.
              setPendingApply(v ?? "pending")
            }
            safetyTimerRef.current = window.setTimeout(silentlyFallBack, 12000)
            // Pass the exact version the pill is advertising so the apply
            // path locks onto the channel-latest and can't land on an
            // older intermediate bundle (the "two taps" trap, 2026-07-04).
            applyPendingCapgoUpdate(capgoNative.latest ?? undefined)
              .then((didApply) => {
                if (!didApply) {
                  silentlyFallBack()
                  return
                }
                // Capgo confirmed the swap is in flight — the webview
                // tear-down should follow within a second or two. But
                // set() sometimes resolves WITHOUT tearing down (bundle
                // marked active, reload deferred to next launch), which
                // used to leave the button on "Updating…" forever.
                // Re-arm the safety timer instead of cancelling it: on a
                // real reload the JS context dies and the timer dies with
                // it; if nothing happens in 15s, fall back to the quiet
                // "finishes next launch" pill like every other failure.
                if (safetyTimerRef.current !== null) {
                  window.clearTimeout(safetyTimerRef.current)
                }
                safetyTimerRef.current = window.setTimeout(silentlyFallBack, 15000)
              })
              .catch(() => silentlyFallBack())
          },
          Icon: RefreshCw,
          allowDismiss: true,
        }
      case 'web-capgo':
      default:
        return {
          title: 'New version available',
          buttonLabel: reloading ? 'Refreshing…' : 'Refresh',
          onAction: () => {
            if (reloading) return
            if (isWorkoutInProgress()) {
              window.alert("You have a workout in progress — refresh after you finish it so nothing is interrupted.")
              return
            }
            snoozeCapgoPill()
            setReloading(true)
            forceReloadToLatest()
          },
          Icon: RefreshCw,
          allowDismiss: true,
        }
    }
  })()

  return (
    // Pinned near the TOP so it's noticed and doesn't collide with the bottom
    // tab bar or the sticky workout bar (founder call 2026-07-26). Offset by
    // ~4rem below the safe-area so it tucks JUST UNDER a page's own top header
    // (back button / title) instead of covering it — the app's sticky headers
    // (MainApp, NotificationsPage) are ~56px tall below the notch. Centered with
    // left/right/mx-auto rather than -translate-x-1/2 so the slide-down
    // transform doesn't fight the horizontal centering during the animation.
    <div
      className="fixed left-0 right-0 mx-auto z-[200] w-[calc(100%-2rem)] max-w-sm flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-lg animate-slide-down"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 4rem)" }}
      role="status"
      aria-live="polite"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
        <variant.Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">
          {variant.title}
        </p>
        {update.detail && (
          <p className="text-xs text-muted-foreground leading-tight mt-0.5 truncate">
            {update.detail}
          </p>
        )}
      </div>
      {variant.buttonLabel && (
        <Button
          size="sm"
          onClick={variant.onAction}
          disabled={reloading}
          className="shrink-0 h-8 px-3 text-xs rounded-full"
        >
          {variant.buttonLabel}
        </Button>
      )}
      {variant.allowDismiss && (
        <button
          onClick={persistDismiss}
          className="shrink-0 w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss update notice"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

// Web Capgo version string is `Date.now().toString()` from vite.config —
// display as a short human-readable timestamp instead of a 13-digit blob.
function shortVersion(v: string): string {
  const n = Number(v)
  if (Number.isFinite(n) && n > 1_000_000_000_000) {
    try {
      const d = new Date(n)
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    } catch { /* fall through */ }
  }
  return v.length > 8 ? `…${v.slice(-6)}` : v
}

function webDetail(currentVersion: string | null, latestVersion: string | null): string | null {
  if (!currentVersion || !latestVersion) return null
  return `${shortVersion(currentVersion)} → ${shortVersion(latestVersion)}`
}
